import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getClasses } from '../services/classService';
import { useAuth } from './AuthContext';
import { useCacheInvalidation } from './CacheInvalidationContext';

const ClassContext = createContext();

export const useClasses = () => {
    const context = useContext(ClassContext);
    if (!context) {
        throw new Error('useClasses must be used within a ClassProvider');
    }
    return context;
};

export const ClassProvider = ({ children }) => {
    const { center, hasPermission } = useAuth();
    const { registerRefresh } = useCacheInvalidation();
    const [classes, setClasses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchClasses = useCallback(async () => {
        if (!center) {
            setLoading(false);
            return;
        }

        // Skip fetch if user doesn't have permission
        if (!hasPermission('classes:list')) {
            setLoading(false);
            setError(null);
            setClasses([]);
            return;
        }

        try {
            setLoading(true);
            setError(null);
            // Fetch all classes without filters for caching
            const data = await getClasses();
            setClasses(data);
        } catch (err) {
            setError(err);
            setClasses([]);
        } finally {
            setLoading(false);
        }
    }, [center, hasPermission]);

    // Fetch classes when center changes
    useEffect(() => {
        fetchClasses();
    }, [fetchClasses]);

    // Poll for updates every 10 minutes (when page is visible)
    useEffect(() => {
        const pollInterval = setInterval(() => {
            if (document.visibilityState === 'visible') {
                fetchClasses();
            }
        }, 10 * 60 * 1000); // 10 minutes

        return () => clearInterval(pollInterval);
    }, [fetchClasses]);

    // Refresh function to be called after CRUD operations
    const refreshClasses = useCallback(() => {
        fetchClasses();
    }, [fetchClasses]);

    // Register refresh function for cache invalidation
    useEffect(() => {
        const cleanup = registerRefresh('classes', refreshClasses);
        return cleanup;
    }, [registerRefresh, refreshClasses]);

    const value = {
        classes,
        loading,
        error,
        refreshClasses,
        fetchClasses
    };

    return (
        <ClassContext.Provider value={value}>
            {children}
        </ClassContext.Provider>
    );
};

export default ClassContext;
